import moment from 'moment'
import { get } from 'lodash'
import { LEVELS, AJ_MIN_DATES } from 'constants/enums'

const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance()

const isEmpty = value => value === undefined || value === null || value === ''
const isPhoneEmpty = value =>
  value === undefined || value === null || value === '' || value === '+'
const join = rules => (value, data) =>
  rules.map(rule => rule(value, data)).filter(error => Boolean(error))[0]

const checkEmail = value => {
  return !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    value
  )
}

const checkPasswordStrength = value => {
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&_])[A-Za-z\d@$!%*?&_]{8,}$/.test(value)
}

export function checkRegex(value, regex) {
  if (!regex.test(value)) {
    return {
      text: 'Invalid format',
      intlKey: 'errors.invalid_format'
    }
  }
}

export function phone(value = '') {
  let isValid = true

  if (isEmpty(value)) {
    return
  }

  value = value.replace(/[^0-9]/g, '')
  value = `+${value}`

  try {
    value = phoneUtil.parse(value, '')
    isValid = phoneUtil.isValidNumber(value)
    if (!isValid) {
      return {
        text: 'Invalid phone format',
        intlKey: 'errors.invalid_phone_format',
        values: {}
      }
    }
  } catch (e) {
    return {
      text: 'Invalid phone format',
      intlKey: 'errors.invalid_phone_format',
      values: {}
    }
  }
}

export function emailOrPhoneRequired(e, p) {
  const empty = value =>
    isEmpty(value) || (typeof value === 'string' && !value.trim())

  if (empty(e) && empty(p)) {
    return {
      text: 'Email or phone is required',
      intlKey: 'errors.email_or_phone_required',
      values: {}
    }
  }
}

export function qualificationOrExperienceRequired(e, p) {
  const empty = value =>
      isEmpty(value) || (typeof value === 'string' && !value.trim())

  if (empty(e) && empty(p)) {
    return {
      text: 'Qualification or experience is required',
      intlKey: 'errors.qualification_or_experience_required',
      values: {}
    }
  }
}


export function email(value) {
  if (!isEmpty(value) && checkEmail(value)) {
    return {
      text: 'Invalid email address',
      intlKey: 'errors.invalid_email_address',
      values: {}
    }
  }
}
export function emailsDiffer(value, otherValues) {
  if (!isEmpty(value) && otherValues.some(v => (v === value))) {
    return {
      text: 'Emails cannot match',
      intlKey: 'errors.emails_cannot_match',
      values: {}
    }
  }
}

export function isEmail(value) {
  if (!isEmpty(value) && checkEmail(value)) {
    return false
  }
  return true
}

export function required(value) {
  if (isEmpty(value) || (typeof value === 'string' && !value.trim())) {
    return {
      text: 'Required field',
      intlKey: 'errors.required_field',
      values: {}
    }
  }
}

export function isPasswordStrength(value) {
  return checkPasswordStrength(value)
}

export function requiredPhone(value) {
  if (isPhoneEmpty(value)) {
    return {
      text: 'Required field',
      intlKey: 'errors.required_field',
      values: {}
    }
  }
}

export function requiredCheck(value) {
  if (!value) {
    return {
      text: 'Required field',
      intlKey: 'errors.required_field',
      values: {}
    }
  }
}

export function minLength(min) {
  return value => {
    if (!isEmpty(value) && value.length < min) {
      return {
        text: 'Must be at least {min} characters',
        intlKey: 'errors.must_be_at_least',
        values: { min }
      }
    }
  }
}

export function maxLength(max) {
  return value => {
    if (!isEmpty(value) && value.length > max) {
      return {
        text: 'Must be no more than {max} characters',
        intlKey: 'errors.must_be_no_more_than',
        values: { max }
      }
    }
  }
}

export function integer(value) {
  if (!Number.isInteger(Number(value))) {
    return {
      text: 'Must be an integer',
      intlKey: 'errors.must_be_an_integer',
      values: {}
    }
  }
}

export function oneOf(enumeration) {
  return value => {
    if (enumeration.indexOf(value) !== -1) {
      return {
        text: 'Must be one of: {enums}',
        intlKey: 'errors.must_be_one_of',
        values: { enums: enumeration.join(', ') }
      }
    }
  }
}

export function match(field) {
  return (value, data) => {
    if (data) {
      if (value !== data[field]) {
        return {
          text: 'Do not match',
          intlKey: 'errors.do_not_match'
        }
      }
    }
  }
}

export function moreThan(limit) {
  return value => {
    if (value <= limit) {
      return {
        text: 'Must be more than {limit}',
        intlKey: 'errors.must_be_more_than',
        values: {
          limit
        }
      }
    }
  }
}

export function lessThan(limit) {
  return value => {
    if (value > limit) {
      return {
        text: 'Must be less than {limit}',
        intlKey: 'errors.must_be_less_than',
        values: {
          limit
        }
      }
    }
  }
}

export function logsMin(limit) {
  return value => {
    if (value < limit) {
      return {
        text: 'Minimal duration is 30 min',
        intlKey: 'errors.minimal_duration_is',
        values: {
          limit
        }
      }
    }
  }
}

export function dates(startDate) {
  return endDate => {
    if (moment(startDate).isAfter(endDate)) {
      return {
        text: 'End date must be after start date',
        intlKey: 'errors.end_date_must_be_after_start_date'
      }
    }
  }
}

export function hasNumbers(value) {
  if (!/\d/.test(value)) {
    return {
      text: 'Must contain at least one number',
      intlKey: 'errors.must_contain_at_least',
      values: {}
    }
  }
}

export function hasUpperLowerCase(value) {
  if (!/[A-Z]/.test(value) || !/[a-z]/.test(value)) {
    return {
      text: 'Must include at least one upper case characters',
      intlKey: 'errors.must_include_at_least',
      values: {}
    }
  }
}

export function hasSpecialCharacters(value, passwordSpecChars) {

  let escaped = passwordSpecChars.replace(/[\\"']/g, '\\$&').replace(/\u0000/g, '\\0').replace(/[\/]/g, '\\/').replace(/[\]]/g, '\\]')

  let regex = new RegExp('[' + escaped + ']')

  if (!regex.test(value)) {
    return {
      text: 'Must include at least one upper case characters',
      intlKey: 'errors.include_at_least_one_special_char',
      values: {}
    }
  }
}

export function minNights(startDate, endDate, options = {}) {
  const nights = moment(endDate).diff(startDate, 'days')

  switch (get(options, 'awardLevelValue')) {
    case LEVELS.GOLD.value:
      if (AJ_MIN_DATES.gold.nights > nights) {
        return {
          text: 'A minimum of {nights} nights is required',
          intlKey: 'errors.min_nights_required',
          values: {
            nights: AJ_MIN_DATES.gold.nights
          }
        }
      }
      break
    case LEVELS.SILVER.value:
      if (AJ_MIN_DATES.silver.nights > nights) {
        return {
          text: 'A minimum of {nights} nights is required',
          intlKey: 'errors.min_nights_required',
          values: {
            nights: AJ_MIN_DATES.silver.nights
          }
        }
      }
      break
    case LEVELS.BRONZE.value:
      if (AJ_MIN_DATES.bronze.nights > nights) {
        return {
          text: 'A minimum of {nights} nights is required',
          intlKey: 'errors.min_nights_required',
          values: {
            nights: AJ_MIN_DATES.bronze.nights
          }
        }
      }
      break

    default:
  }
}

export function createValidator(rules) {
  return (data = {}) => {
    const errors = {}

    Object.keys(rules).forEach(key => {
      // concat enables both functions and arrays of functions
      const rule = join([].concat(rules[key]))
      const error = rule(data[key], data)

      if (error) {
        errors[key] = error
      }
    })
    return errors
  }
}
